import React, { useState } from "react";
import { useLang } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Inbox, ChevronDown, ChevronUp, Clock, RefreshCw, CheckCircle2 } from "lucide-react";

/**
 * A20-P12 — Pure presentation component for the owner-side offer
 * comparator. Receives a normalised offers[] (already projected by the
 * backend to the owner-safe set) and renders a side-by-side comparison.
 *
 * No fetching, no mutation. No "Select offer" or "Contact vendor" — those
 * belong to P13. No staff_notes, no audit data, no vendor PII.
 */
const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `€${Math.round(Number(n) || 0)}`; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
    const ms = d.getTime() - Date.now();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

const ExpandableText = ({ text, lines = 2, idPrefix }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-slate-300">—</span>;
  // Heuristic: long enough to be worth collapsing.
  const needsToggle = text.length > 80;
  return (
    <div className="text-sm text-slate-700">
      <p
        className={open || !needsToggle ? "whitespace-pre-wrap" : ""}
        style={open || !needsToggle ? {} : {
          display: "-webkit-box",
          WebkitLineClamp: lines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {text}
      </p>
      {needsToggle && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-emerald-700 hover:text-emerald-900 mt-1"
          data-testid={`${idPrefix}-toggle`}
        >
          {open ? (
            <span className="inline-flex items-center gap-1"><ChevronUp className="w-3 h-3" />{t("marketplace.offers.col.collapse")}</span>
          ) : (
            <span className="inline-flex items-center gap-1"><ChevronDown className="w-3 h-3" />{t("marketplace.offers.col.expand")}</span>
          )}
        </button>
      )}
    </div>
  );
};

const TagList = ({ items, idPrefix, max = 5 }) => {
  const { t } = useLang();
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-slate-300">—</span>;
  }
  const visible = items.slice(0, max);
  const extra = items.length - visible.length;
  return (
    <ul className="space-y-1" data-testid={`${idPrefix}-list`}>
      {visible.map((s, i) => (
        <li key={`${idPrefix}-${i}`} className="text-sm text-slate-700 leading-snug">
          • {s}
        </li>
      ))}
      {extra > 0 && (
        <li className="text-xs text-slate-500" data-testid={`${idPrefix}-more`}>
          {t("marketplace.offers.col.moreN").replace("{n}", String(extra))}
        </li>
      )}
    </ul>
  );
};

const ExpirationChip = ({ iso, idPrefix }) => {
  const { t } = useLang();
  if (!iso) return null;
  const days = daysUntil(iso);
  if (days === null) return <span className="text-xs text-slate-500">{iso}</span>;
  if (days <= 0) {
    return (
      <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid={`${idPrefix}-expired`}>
        {t("marketplace.offers.col.expiresSoon")}
      </Badge>
    );
  }
  const tone = days <= 3 ? "bg-rose-100 text-rose-800" : days <= 7 ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800";
  return (
    <Badge className={`${tone} text-[10px]`} data-testid={`${idPrefix}-days`}>
      {t("marketplace.offers.col.expiresInDays").replace("{n}", String(days))}
    </Badge>
  );
};

const OfferCard = ({ offer, idPrefix, requestStatus, selectedOfferId, onSelectClick }) => {
  const { t } = useLang();
  const tid = `${idPrefix}-${offer.id}`;
  const isSelected = offer.status === "selected";
  const someoneSelected = requestStatus === "offer_selected" || !!selectedOfferId;
  const isExpired = (() => {
    const d = daysUntil(offer.expiration_date);
    return d !== null && d <= 0;
  })();
  // CTA visibility: only when request is approved_internal AND nothing
  // has been selected yet on this request. Once selection lands, the
  // sibling cards switch to a greyed-out "Not selected" state.
  const canShowCta = requestStatus === "approved_internal" && !someoneSelected;
  const ctaDisabled = isExpired;

  return (
    <div
      className={[
        "border rounded-lg p-4 shadow-sm flex flex-col gap-3 min-w-[260px] transition-opacity",
        isSelected ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-300" : "bg-white",
        someoneSelected && !isSelected ? "opacity-60" : "",
      ].join(" ")}
      data-testid={tid}
      data-offer-status={offer.status}
    >
      {isSelected && (
        <div className="-mx-1 -mt-1 mb-1 flex items-center justify-between">
          <Badge className="bg-emerald-700 text-white inline-flex items-center gap-1"
                 data-testid={`${tid}-selected-badge`}>
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t("marketplace.offers.selectedBadge")}
          </Badge>
          {offer.selected_at && (
            <span className="text-[10px] text-emerald-800">
              {t("marketplace.offers.selectedOn").replace("{date}", String(offer.selected_at).slice(0, 10))}
            </span>
          )}
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("marketplace.offers.col.vendor")}
        </div>
        <div className="text-base font-semibold text-slate-900 truncate" title={offer.vendor_name_snapshot}
             data-testid={`${tid}-vendor`}>
          {offer.vendor_name_snapshot || "—"}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("marketplace.offers.col.price")}
        </div>
        <div className="text-2xl font-bold text-emerald-800 tabular-nums" data-testid={`${tid}-price`}>
          {fmtEur(offer.offered_price)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs text-slate-600">
          {t("marketplace.offers.col.expires")}: <span className="font-mono">{offer.expiration_date || "—"}</span>
        </div>
        <ExpirationChip iso={offer.expiration_date} idPrefix={`${tid}-exp`} />
      </div>

      {typeof offer.response_time_hours === "number" && (
        <div className="text-xs text-slate-600 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {t("marketplace.offers.col.responseTime")}:{" "}
          <span className="font-semibold">
            {t("marketplace.offers.col.hoursN").replace("{n}", String(offer.response_time_hours))}
          </span>
        </div>
      )}

      {(offer.availability_start || offer.availability_end) && (
        <div className="text-xs text-slate-600">
          {t("marketplace.offers.col.availability")}:{" "}
          <span className="font-mono">{offer.availability_start || "—"} → {offer.availability_end || "—"}</span>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wide text-emerald-700">
          {t("marketplace.offers.col.included")}
        </div>
        <TagList items={offer.included_services} idPrefix={`${tid}-incl`} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-rose-700">
          {t("marketplace.offers.col.excluded")}
        </div>
        <TagList items={offer.exclusions} idPrefix={`${tid}-excl`} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("marketplace.offers.col.delivery")}
        </div>
        <ExpandableText text={offer.delivery_conditions} idPrefix={`${tid}-deliv`} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("marketplace.offers.col.cancellation")}
        </div>
        <ExpandableText text={offer.cancellation_policy} idPrefix={`${tid}-canc`} />
      </div>

      {offer.language && (
        <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          {t("marketplace.offers.col.language")}:
          <Badge className="bg-slate-100 text-slate-700 uppercase text-[10px]">{offer.language}</Badge>
        </div>
      )}

      {/* A20-P13 — CTA / lock state. Three branches:
            1. CTA visible & clickable
            2. CTA hidden because request already has a selection (sibling grey-out)
            3. Selected — show muted "Selected" line */}
      {canShowCta ? (
        <Button
          onClick={() => onSelectClick && onSelectClick(offer)}
          disabled={ctaDisabled}
          className="mt-1 bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
          data-testid={`${tid}-select-cta`}
        >
          {ctaDisabled ? t("marketplace.offers.selectCtaExpired") : t("marketplace.offers.selectCta")}
        </Button>
      ) : someoneSelected && !isSelected ? (
        <div className="mt-1 text-xs text-slate-500 italic text-center" data-testid={`${tid}-not-selected`}>
          {t("marketplace.offers.notSelectedHint")}
        </div>
      ) : isSelected ? (
        <div className="mt-1 text-xs text-emerald-800 font-medium text-center inline-flex items-center justify-center gap-1"
             data-testid={`${tid}-selected-foot`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t("marketplace.offers.selectedBadge")}
        </div>
      ) : null}
    </div>
  );
};

export const OfferComparator = ({
  data,
  loading,
  onRefresh,
  onSelectOffer,
  testIdPrefix = "offer-comparator",
}) => {
  const { t } = useLang();
  const offers = data?.offers || [];
  const capped = !!data?.capped;
  const requestStatus = data?.request?.status;
  const selectedOfferId = data?.request?.selected_offer_id || null;
  const showSelectedBanner = requestStatus === "offer_selected";

  // A20-P13 — confirmation modal state. Lifted here so the AlertDialog
  // renders exactly once regardless of how many offer cards exist.
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!confirmOffer || !onSelectOffer) {
      setConfirmOffer(null);
      return;
    }
    setSubmitting(true);
    try {
      await onSelectOffer(confirmOffer);
    } finally {
      setSubmitting(false);
      setConfirmOffer(null);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm" data-testid={`${testIdPrefix}-loading`}>
        …
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="py-10 px-4 text-center" data-testid={`${testIdPrefix}-empty`}>
        <Inbox className="w-10 h-10 mx-auto text-slate-300" />
        <h3 className="mt-3 text-base font-semibold text-slate-800">
          {t("marketplace.offers.emptyTitle")}
        </h3>
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
          {t("marketplace.offers.emptyBody")}
        </p>
        {onRefresh && (
          <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}
                  data-testid={`${testIdPrefix}-refresh-empty`}>
            <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("marketplace.offers.refresh")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {showSelectedBanner && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2"
             data-testid={`${testIdPrefix}-selected-banner`}>
          <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <p className="text-sm text-emerald-900">
            {t("marketplace.offers.selectedBanner")}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-slate-600">
          {offers.length === 1
            ? t("marketplace.offers.countOne")
            : t("marketplace.offers.countMany").replace("{n}", String(offers.length))}
        </div>
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh}
                  data-testid={`${testIdPrefix}-refresh`}>
            <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("marketplace.offers.refresh")}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 sm:auto-cols-[minmax(280px,1fr)]">
          {offers.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              idPrefix={`${testIdPrefix}-card`}
              requestStatus={requestStatus}
              selectedOfferId={selectedOfferId}
              onSelectClick={onSelectOffer ? (offer) => setConfirmOffer(offer) : undefined}
            />
          ))}
        </div>
      </div>

      {capped && (
        <p className="text-xs text-slate-500 pt-2 border-t" data-testid={`${testIdPrefix}-capped`}>
          {t("marketplace.offers.capped")}
        </p>
      )}

      {/* A20-P13 — single confirmation modal shared by all cards. */}
      <AlertDialog open={!!confirmOffer} onOpenChange={(open) => !open && !submitting && setConfirmOffer(null)}>
        <AlertDialogContent data-testid={`${testIdPrefix}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("marketplace.offers.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {t("marketplace.offers.confirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmOffer && (
            <div className="rounded-md bg-slate-50 p-3 text-sm border">
              <div className="font-semibold text-slate-900">{confirmOffer.vendor_name_snapshot}</div>
              <div className="text-emerald-800 font-bold text-lg">{fmtEur(confirmOffer.offered_price)}</div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting} data-testid={`${testIdPrefix}-confirm-cancel`}>
              {t("marketplace.offers.confirmCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              disabled={submitting}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid={`${testIdPrefix}-confirm-confirm`}
            >
              {t("marketplace.offers.confirmConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OfferComparator;
